import { ExternalLink, Trash2, MoreVertical, FolderPlus } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import type { FavoriteItem, FavoriteList } from '@/types/favorites';

interface FavoriteItemCardActionsProps {
  item: FavoriteItem;
  lists: FavoriteList[];
  onRemove: () => void;
  onToggleList: (listId: string) => void;
}

export function FavoriteItemCardActions({
  item,
  lists,
  onRemove,
  onToggleList,
}: FavoriteItemCardActionsProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showListMenu, setShowListMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        setShowListMenu(false);
      }
    };

    if (showMenu || showListMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu, showListMenu]);

  return (
    <div className="relative" ref={menuRef} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }}
        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <MoreVertical size={16} />
      </button>

      {showMenu && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20 min-w-[160px]">
          {/* Add to List */}
          <div className="relative">
            <button
              onClick={() => setShowListMenu(!showListMenu)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
            >
              <FolderPlus size={14} />
              <span>Add to list</span>
            </button>

            {showListMenu && lists.length > 0 && (
              <div className="absolute left-full top-0 ml-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[150px]">
                {lists.map((list) => {
                  const isInList = item.listIds.includes(list.id);
                  return (
                    <button
                      key={list.id}
                      onClick={() => {
                        onToggleList(list.id);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                        isInList
                          ? 'bg-[#f8e8f8] text-[#8e4585]'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <span className="truncate">{list.name}</span>
                      {isInList && <span className="ml-auto">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Open Link (for resources) */}
          {item.itemType === 'resource' && (
            <a
              href={item.itemId} // For resources, itemId is the URL
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
              onClick={() => setShowMenu(false)}
            >
              <ExternalLink size={14} />
              <span>Open link</span>
            </a>
          )}

          {/* Remove */}
          <button
            onClick={onRemove}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 size={14} />
            <span>Remove</span>
          </button>
        </div>
      )}
    </div>
  );
}
